import { ApolloCache } from '@apollo/client';
import { Query } from './inopack-graphql-schema';

export function clearClientQueryCache(
    cache: ApolloCache<any>,
    key: keyof Query,
) {
    cache.evict({
        id: 'ROOT_QUERY',
        fieldName: key,
        broadcast: true,
    });
}

export function clearMaintenanceModule(cache: ApolloCache<any>) {
    clearClientQueryCache(cache, 'getMachines');
    clearClientQueryCache(cache, 'getMachine');
    clearClientQueryCache(cache, 'getMachinePart');
    clearClientQueryCache(cache, 'getMachineParts');
    clearClientQueryCache(cache, 'getMachineSection');
    clearClientQueryCache(cache, 'getMachineSections');
    clearClientQueryCache(cache, 'getSpares');
    clearClientQueryCache(cache, 'getSpare');
    clearClientQueryCache(cache, 'getSpareCategories');
    clearClientQueryCache(cache, 'getSpareCategory');
    clearClientQueryCache(cache, 'getSpareAdjustments');
    clearClientQueryCache(cache, 'getSpareOperation');
    clearClientQueryCache(cache, 'getSpareTransactions');
    cache.gc();
}

export function clearProductionModule(cache: ApolloCache<any>) {
    // order production
    clearClientQueryCache(cache, 'getOrderProduction');
    clearClientQueryCache(cache, 'getMachine');
    clearClientQueryCache(cache, 'getMachines');
    clearClientQueryCache(cache, 'paginatedMachines');
    clearClientQueryCache(cache, 'paginatedOrderProductions');

    // order adjustments
    clearClientQueryCache(cache, 'getOrderAdjustment');
    clearClientQueryCache(cache, 'getOrderAdjustments');
    clearClientQueryCache(cache, 'paginatedOrderAdjustments');

    //employees
    clearClientQueryCache(cache, 'getEmployee');
    clearClientQueryCache(cache, 'getEmployees');

    // summary
    clearClientQueryCache(cache, 'getProductionSummary');

    // products
    clearProducts(cache);
    cache.gc();
}

export function clearSalesModule(cache: ApolloCache<any>) {
    // accounts
    clearClientQueryCache(cache, 'getAccount');
    clearClientQueryCache(cache, 'getAccounts');
    clearClientQueryCache(cache, 'paginatedAccounts');

    // client statement (sales use net totals, transfers pay them)
    clearClientQueryCache(cache, 'getAccountTransactionHistory');
    clearClientQueryCache(cache, 'getAccountOpeningBalance');
    clearClientQueryCache(cache, 'getAccountTransfers');

    // order requests
    clearClientQueryCache(cache, 'getOrderRequests');
    clearClientQueryCache(cache, 'getOrderRequest');
    clearClientQueryCache(cache, 'paginatedOrderRequests');
    clearClientQueryCache(cache, 'getOrderRequestMaxOrderCode');

    // order quotations
    clearClientQueryCache(cache, 'getOrderQuotation');
    clearClientQueryCache(cache, 'getOrderQuotations');
    clearClientQueryCache(cache, 'paginatedOrderQuotations');
    clearClientQueryCache(cache, 'getOrderQuotationMaxOrderCode');
    clearClientQueryCache(cache, 'getOrderQuotationCatalogChanges');
    clearClientQueryCache(cache, 'isOrderQuotationCodeOccupied');

    // account product catalog (drives the order-request product picker)
    clearClientQueryCache(cache, 'getAccountProducts');

    // order sales
    clearClientQueryCache(cache, 'getOrderSale');
    clearClientQueryCache(cache, 'getOrderSales');
    clearClientQueryCache(cache, 'getOrderSalesWithDisparities');
    clearClientQueryCache(cache, 'paginatedOrderSales');
    clearClientQueryCache(cache, 'getOrderSaleMaxOrderCode');

    // summary
    clearClientQueryCache(cache, 'getSalesSummary');

    clearProducts(cache);
    cache.gc();
}

function clearProducts(cache: ApolloCache<any>) {
    clearClientQueryCache(cache, 'getProducts');
    clearClientQueryCache(cache, 'getProduct');
    clearClientQueryCache(cache, 'paginatedProducts');
    clearClientQueryCache(cache, 'getProductsInventory');
    clearClientQueryCache(cache, 'getOptimizedRequestProducts');
}

export function clearManagement(cache: ApolloCache<any>) {
    clearClientQueryCache(cache, 'paginatedTransfers');
    clearClientQueryCache(cache, 'getTransfer');
    clearClientQueryCache(cache, 'getTransfers');
    clearClientQueryCache(cache, 'paginatedExpenses');
    clearClientQueryCache(cache, 'getExpenses');
    clearClientQueryCache(cache, 'getExpense');
    clearClientQueryCache(cache, 'paginatedResources');
    clearClientQueryCache(cache, 'getResources');
    clearClientQueryCache(cache, 'getResource');
    clearClientQueryCache(cache, 'getAccounts');
    clearClientQueryCache(cache, 'getOwnAccountsTransferSummary');
    clearClientQueryCache(cache, 'getThirdPartyAccountsTransferSummary');
    clearClientQueryCache(cache, 'getExpensesSummary');
    clearClientQueryCache(cache, 'getExpenseResources');
    clearSalesModule(cache);
}

export function clearActivitiesModule(cache: ApolloCache<any>) {
    // The activities page. Evicting the field does not touch the reader's
    // pagination: `skip` is React state, so they stay on the page they were on
    // and simply get fresh rows.
    clearClientQueryCache(cache, 'paginatedActivities');

    // Every open Historial panel. `evict` with a fieldName and no args removes
    // ALL cached argument variants of that field, which is what is wanted here:
    // one activity can belong to any (entity_name, entity_id) pair, and the
    // subscription payload is not enough to tell which panel is showing it. A
    // mounted panel therefore refetches and the new activity appears live,
    // instead of the panel sitting stale until it is remounted.
    //
    // getActivity is deliberately NOT evicted: an activity row is immutable once
    // written — its snapshots are a frozen before/after — so a cached diff can
    // never go stale, and evicting it would make every open diff refetch on
    // every unrelated activity written anywhere in the system.
    clearClientQueryCache(cache, 'getEntityActivities');

    cache.gc();
}

export function clearAuthModule(cache: ApolloCache<any>) {
    clearClientQueryCache(cache, 'getUser');
    clearClientQueryCache(cache, 'users');
    cache.gc();
}
